import type { CardComparativo, FaturaPainel } from '../types.ts';
import { mesReferenciaParaData } from '../../../../src/lib/financeiro/periodo.ts';
import { valorDevido } from '../../../../src/lib/statusBoleto.ts';
import { ehCobranca } from '../../../../src/lib/financeiro/definicoesKpi.ts';

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

export type MetricaComparadorId =
  | 'faturamento' | 'inadimplencia' | 'boletosEmitidos'
  | 'boletosPagos' | 'boletosVencidos' | 'emAberto' | 'taxaInadimplencia';

/** Um "período" do comparador é um range de mes_referencia, inclusive nas duas pontas.
 *  inicio === fim compara um único mês. */
export interface RangeMeses {
  inicio: string; // mes_referencia, ex. "JAN/2026"
  fim: string;
}

interface ResultadoMetrica {
  valor: number;
  extraNumero: number; // segunda grandeza usada para montar o "extra" do card
  extraNumero2?: number; // só taxaInadimplencia usa (valor emitido, pro extra do período A)
}

interface DefinicaoMetrica {
  id: MetricaComparadorId;
  titulo: string;
  metricaDescricao: string;
  formato: 'moeda' | 'numero' | 'percentual';
  corDirecao: 'positiva' | 'negativa' | 'neutra-fixa'; // 'positiva' = subir é bom, 'negativa' = subir é ruim
  calcular: (faturas: FaturaPainel[]) => ResultadoMetrica;
  formatarExtra: (a: ResultadoMetrica, b: ResultadoMetrica) => string; // combinado, mostrado acima do gráfico
  formatarExtraPeriodo: (r: ResultadoMetrica) => string; // um período só, mostrado sob o grupo de barras dele no gráfico
}

function pct(atual: number, anterior: number): number | null {
  if (anterior === 0) return null;
  return Math.round(((atual - anterior) / anterior) * 1000) / 10;
}

function somaValor(faturas: FaturaPainel[]): number {
  return faturas.reduce((s, f) => s + valorDevido(f), 0);
}

// Mesmo universo de "emitido" dos KPIs: sem isso "Boletos emitidos" contava
// fatura sem boleto (pipeline, valor zero, sem injeção).
const soCobrancas = (faturas: FaturaPainel[]) => faturas.filter(ehCobranca);

function faturasNoRange(dados: FaturaPainel[], range: RangeMeses): FaturaPainel[] {
  const ini = mesReferenciaParaData(range.inicio);
  const fim = mesReferenciaParaData(range.fim);
  if (!ini || !fim) return [];
  return dados.filter((f) => {
    const d = mesReferenciaParaData(f.mes_referencia);
    return d ? d.getTime() >= ini.getTime() && d.getTime() <= fim.getTime() : false;
  });
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

// Sequência calendário de mes_referencia entre inicio e fim, inclusive — inclui meses sem
// nenhuma fatura (diferente de "mesesDisponiveis" do componente, que só lista meses com dado
// real). Usada pra montar o detalhe mês a mês do gráfico, sem buraco na sequência.
function enumerarMeses(range: RangeMeses): string[] {
  const ini = mesReferenciaParaData(range.inicio);
  const fim = mesReferenciaParaData(range.fim);
  if (!ini || !fim) return [];
  const meses: string[] = [];
  const cursor = new Date(ini.getFullYear(), ini.getMonth(), 1);
  while (cursor.getTime() <= fim.getTime()) {
    meses.push(`${MESES[cursor.getMonth()]}/${cursor.getFullYear()}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return meses;
}

function faturasDoMes(dados: FaturaPainel[], mes: string): FaturaPainel[] {
  return dados.filter((f) => f.mes_referencia === mes);
}

// Ordem canônica de exibição — a saída de calcularComparativoCustomizado sempre segue esta
// ordem, independente da ordem em que as métricas chegam selecionadas.
const DEFINICOES: DefinicaoMetrica[] = [
  {
    id: 'faturamento',
    titulo: 'Faturamento',
    metricaDescricao: 'Valor emitido no período',
    formato: 'moeda',
    corDirecao: 'positiva',
    calcular: (faturas) => {
      const cobrancas = soCobrancas(faturas);
      return { valor: somaValor(cobrancas), extraNumero: cobrancas.length };
    },
    formatarExtra: (a, b) => `${a.extraNumero} → ${b.extraNumero} boletos`,
    formatarExtraPeriodo: (r) => `${r.extraNumero} boletos`,
  },
  {
    id: 'inadimplencia',
    titulo: 'Inadimplência',
    metricaDescricao: 'Valor vencido no período',
    formato: 'moeda',
    corDirecao: 'negativa',
    calcular: (faturas) => {
      const vencidos = faturas.filter((f) => f.statusDerivado === 'Vencido');
      return { valor: somaValor(vencidos), extraNumero: vencidos.length };
    },
    formatarExtra: (a, b) => `${a.extraNumero} → ${b.extraNumero} boletos vencidos`,
    formatarExtraPeriodo: (r) => `${r.extraNumero} vencidos`,
  },
  {
    id: 'boletosEmitidos',
    titulo: 'Boletos emitidos',
    metricaDescricao: 'Quantidade de boletos no período',
    formato: 'numero',
    corDirecao: 'positiva',
    calcular: (faturas) => {
      const cobrancas = soCobrancas(faturas);
      const total = somaValor(cobrancas);
      const ticketMedio = cobrancas.length > 0 ? total / cobrancas.length : 0;
      return { valor: cobrancas.length, extraNumero: ticketMedio };
    },
    formatarExtra: (a, b) => `Ticket médio: ${fmtBRL(a.extraNumero)} → ${fmtBRL(b.extraNumero)}`,
    formatarExtraPeriodo: (r) => `Ticket médio: ${fmtBRL(r.extraNumero)}`,
  },
  {
    id: 'boletosPagos',
    titulo: 'Boletos pagos',
    metricaDescricao: 'Valor pago no período',
    formato: 'moeda',
    corDirecao: 'positiva',
    calcular: (faturas) => {
      // Quitado fora: o dinheiro dele entrou pelo boleto avulso/unificado, que
      // já está entre os 'Pago'. Ver `estaPago` em src/lib/financeiro/definicoesKpi.ts.
      const pagos = faturas.filter((f) => f.statusDerivado === 'Pago');
      return { valor: somaValor(pagos), extraNumero: pagos.length };
    },
    formatarExtra: (a, b) => `${a.extraNumero} → ${b.extraNumero} boletos pagos`,
    formatarExtraPeriodo: (r) => `${r.extraNumero} pagos`,
  },
  {
    id: 'boletosVencidos',
    titulo: 'Boletos vencidos',
    metricaDescricao: 'Quantidade de boletos vencidos no período',
    formato: 'numero',
    corDirecao: 'negativa',
    calcular: (faturas) => {
      const vencidos = faturas.filter((f) => f.statusDerivado === 'Vencido');
      return { valor: vencidos.length, extraNumero: somaValor(vencidos) };
    },
    formatarExtra: (a, b) => `${fmtBRL(a.extraNumero)} → ${fmtBRL(b.extraNumero)}`,
    formatarExtraPeriodo: (r) => fmtBRL(r.extraNumero),
  },
  {
    id: 'emAberto',
    titulo: 'Em aberto',
    metricaDescricao: 'Valor em aberto no período',
    formato: 'moeda',
    corDirecao: 'neutra-fixa',
    calcular: (faturas) => {
      const abertos = faturas.filter((f) => f.statusDerivado === 'Em aberto');
      return { valor: somaValor(abertos), extraNumero: abertos.length };
    },
    formatarExtra: (a, b) => `${a.extraNumero} → ${b.extraNumero} boletos em aberto`,
    formatarExtraPeriodo: (r) => `${r.extraNumero} em aberto`,
  },
  {
    id: 'taxaInadimplencia',
    titulo: 'Taxa de inadimplência',
    metricaDescricao: 'Vencido ÷ emitido do período',
    formato: 'percentual',
    corDirecao: 'negativa',
    calcular: (faturas) => {
      const emitido = somaValor(soCobrancas(faturas));
      const vencido = somaValor(faturas.filter((f) => f.statusDerivado === 'Vencido'));
      return { valor: emitido > 0 ? vencido / emitido : 0, extraNumero: vencido, extraNumero2: emitido };
    },
    formatarExtra: (a) => `${fmtBRL(a.extraNumero)} vencido de ${fmtBRL(a.extraNumero2 ?? 0)} emitido`,
    formatarExtraPeriodo: (r) => `${fmtBRL(r.extraNumero)} / ${fmtBRL(r.extraNumero2 ?? 0)}`,
  },
];

/** Lista das 7 métricas na ordem canônica — usada pelo componente pra montar os chips
 *  de seleção (só precisa de `id` e `titulo`). */
export const METRICAS_COMPARADOR: { id: MetricaComparadorId; titulo: string }[] =
  DEFINICOES.map(({ id, titulo }) => ({ id, titulo }));

export function calcularComparativoCustomizado(
  dados: FaturaPainel[],
  periodoA: RangeMeses,
  periodoB: RangeMeses,
  metricas: MetricaComparadorId[],
): CardComparativo[] {
  const faturasA = faturasNoRange(dados, periodoA);
  const faturasB = faturasNoRange(dados, periodoB);
  const rotuloA = periodoA.inicio === periodoA.fim ? periodoA.inicio : `${periodoA.inicio} a ${periodoA.fim}`;
  const rotuloB = periodoB.inicio === periodoB.fim ? periodoB.inicio : `${periodoB.inicio} a ${periodoB.fim}`;

  const selecionadas = new Set(metricas);
  return DEFINICOES.filter((def) => selecionadas.has(def.id)).map((def) => {
    const resultadoA = def.calcular(faturasA);
    const resultadoB = def.calcular(faturasB);
    const diferenca = pct(resultadoA.valor, resultadoB.valor);
    const subiu = diferenca !== null && diferenca >= 0;
    const corSemantica: CardComparativo['corSemantica'] =
      def.corDirecao === 'neutra-fixa' || diferenca === null
        ? 'neutra'
        : def.corDirecao === 'positiva'
        ? (subiu ? 'positiva' : 'negativa')
        : (subiu ? 'negativa' : 'positiva');

    const detalheMensal: CardComparativo['detalheMensal'] = [
      ...enumerarMeses(periodoA).map((mes) => ({
        mesReferencia: mes,
        periodo: 'A' as const,
        valor: def.calcular(faturasDoMes(dados, mes)).valor,
      })),
      ...enumerarMeses(periodoB).map((mes) => ({
        mesReferencia: mes,
        periodo: 'B' as const,
        valor: def.calcular(faturasDoMes(dados, mes)).valor,
      })),
    ];

    return {
      titulo: def.titulo,
      metricaDescricao: def.metricaDescricao,
      rotuloAtual: rotuloA,
      rotuloAnterior: rotuloB,
      valorAtual: resultadoA.valor,
      valorAnterior: resultadoB.valor,
      diferencaAbsoluta: resultadoA.valor - resultadoB.valor,
      diferencaPercentual: diferenca,
      corSemantica,
      extra: def.formatarExtra(resultadoA, resultadoB),
      extraAtual: def.formatarExtraPeriodo(resultadoA),
      extraAnterior: def.formatarExtraPeriodo(resultadoB),
      formato: def.formato,
      detalheMensal,
    };
  });
}
